import type { PortOption, TripType } from "@/types/search";
import { validateSearchForm, type SearchErrors } from "../utils/validation";
import type { PassengerCounts, TicketTypeList } from "@/types/init";
import { useState } from "react";
import { useScheduleTripSearch } from "./useScheduleTripSearch";
import { toast } from "sonner";

const NO_ERRORS: SearchErrors = {
  origin: false,
  destination: false,
  departDate: false,
  returnDate: false,
  passenger: false,
};

type Args = {
  tripType: TripType;
  selectedOrigin?: PortOption;
  selectedDestination?: PortOption;
  departDate: Date;
  returnDate?: Date;
  counts: PassengerCounts;
  passengerTypes: TicketTypeList[];
  totalPax: number;
  onSubmitted?: () => void;
};

export function usePurchaseTicketSearchForm({
  tripType,
  selectedOrigin,
  selectedDestination,
  departDate,
  returnDate,
  counts,
  passengerTypes,
  totalPax,
  onSubmitted,
}: Args) {
  const [errors, setErrors] = useState<SearchErrors>(NO_ERRORS);
  const { handleSearch, loading: searchLoading } =
    useScheduleTripSearch();

  const clearError = (field: keyof SearchErrors) =>
    setErrors((prev) => ({ ...prev, [field]: false }));

  const handlePaxChange = (value: PassengerCounts) => {
    const total = Object.values(value).reduce((a, b) => a + b, 0);
    if (total > 0) clearError("passenger");
  };

  const handleSubmit = async () => {
    const result = validateSearchForm({
      origin: selectedOrigin,
      destination: selectedDestination,
      departDate,
      returnDate,
      tripType,
      totalPax,
    });

    setErrors(result.errors);

    if (!result.valid) {
      toast.error(result.message);
      return false;
    }

    const success = await handleSearch({
      tripType,
      origin: selectedOrigin,
      destination: selectedDestination,
      departDate,
      returnDate,
      counts,
      passengerTypes,
    });

    if (success) onSubmitted?.();

    return success;
  };

  return {
    errors,
    clearError,
    searchLoading,
    handlePaxChange,
    handleSubmit,
  };
}
